import React, { useEffect, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { PromptInjectionLLMCard } from '../Dashboard/PromptInjection/PromptInjectionLLMCard';
import { PromptInjectionMLCard } from '../Dashboard/PromptInjection/PromptInjectionMLCard';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectProjectResults,
  selectSelectedVersion,
  setSelectedVersion
} from '../project-details/data-access/project.reducer';
import { Box, Chip, Button } from '@mui/material';
import VersionHistory, { VersionItem } from '../../../components/common/VersionHistory';
import { isWorkbenchNonRerunRun } from '../../../utils/workbenchRunFilter';

export interface PromptInjectionRef {
  handleHistoryOpen: () => void;
}

const hasLlmPayload = (v: Record<string, unknown>) =>
  Boolean(v.LLM_PROMPT_INJECTION_ANALYSIS || v.llm_prompt_injection_analysis);

const hasMlPayload = (v: Record<string, unknown>) =>
  Boolean(v.ML_PROMPT_INJECTION_ANALYSIS || v.ml_prompt_injection_analysis);

interface PromptInjectionProps {
  showInlineVersionControls?: boolean;
  onVersionsReady?: (info: { total: number; runsWithMl: number; runsWithLlm: number }) => void;
}

const PromptInjection = forwardRef<PromptInjectionRef, PromptInjectionProps>(
  ({ showInlineVersionControls = true, onVersionsReady }, ref) => {
    const dispatch = useDispatch();
    const projectResults = useSelector(selectProjectResults);
    const selectedVersion = useSelector(selectSelectedVersion);

    const [historyOpen, setHistoryOpen] = useState(false);
    const [promptInjectionVersions, setPromptInjectionVersions] = useState<any[]>([]);

    useImperativeHandle(ref, () => ({
      handleHistoryOpen: () => setHistoryOpen(true)
    }));

    useEffect(() => {
      const versions: any[] = [];
      const seen = new Set<string>();

      const push = (candidate: unknown) => {
        if (!candidate || typeof candidate !== 'object') return;
        if (Array.isArray(candidate)) {
          candidate.forEach(item => {
            if (item && typeof item === 'object' && isWorkbenchNonRerunRun(item)) {
              const v = item as any;
              const id = v.result_id || (v.title && `${v.title}-${String(v.timestamp ?? '')}`) || `pi-${seen.size}`;
              if (id && !seen.has(id)) {
                seen.add(id);
                versions.push({ ...v, result_id: v.result_id || id });
              }
            }
          });
        } else {
          const v = candidate as any;
          if (!isWorkbenchNonRerunRun(v)) return;
          const id = v.result_id || (v.title && `${v.title}-${String(v.timestamp ?? '')}`) || `pi-${seen.size}`;
          if (id && !seen.has(id)) {
            seen.add(id);
            versions.push({ ...v, result_id: v.result_id || id });
          }
        }
      };

      push(projectResults?.results?.prompt_injection);
      push(projectResults?.collection_results?.prompt_injection);
      push(projectResults?.feature_results?.prompt_injection);
      setPromptInjectionVersions(versions);

      let runsWithMl = 0;
      let runsWithLlm = 0;
      for (const row of versions) {
        const r = row as Record<string, unknown>;
        if (hasMlPayload(r)) runsWithMl += 1;
        if (hasLlmPayload(r)) runsWithLlm += 1;
      }
      onVersionsReady?.({ total: versions.length, runsWithMl, runsWithLlm });
    }, [projectResults, onVersionsReady]);

    const versionItems: VersionItem[] = useMemo(() => {
      return promptInjectionVersions.map((version, index) => {
        const ts = version.timestamp;
        let dateStr = 'Custom Run';
        if (ts) {
          if (typeof ts === 'object' && ts.$date) {
            dateStr = new Date(ts.$date).toISOString();
          } else if (typeof ts === 'string') {
            dateStr = ts;
          }
        }
        return {
          id: version.result_id ?? `pi-${index}`,
          timestamp: dateStr,
          date: dateStr,
          modifier: (version.userId as string) || 'System',
          isCurrent: selectedVersion?.result_id === version.result_id,
          title: (version.title as string) || `Prompt Injection Run ${index + 1}`
        };
      });
    }, [promptInjectionVersions, selectedVersion]);

    const handleVersionHistorySelect = (item: VersionItem) => {
      const raw = promptInjectionVersions.find(v => v.result_id === item.id);
      if (raw) dispatch(setSelectedVersion(raw));
      setHistoryOpen(false);
    };

    const isVersionMatch = useMemo(() => {
      return promptInjectionVersions.some(v => v.result_id === selectedVersion?.result_id);
    }, [promptInjectionVersions, selectedVersion]);

    const llmAnalysis = isVersionMatch
      ? selectedVersion?.LLM_PROMPT_INJECTION_ANALYSIS || selectedVersion?.llm_prompt_injection_analysis
      : undefined;
    const mlAnalysis = isVersionMatch
      ? selectedVersion?.ML_PROMPT_INJECTION_ANALYSIS || selectedVersion?.ml_prompt_injection_analysis
      : undefined;

    return (
      <div>
        {showInlineVersionControls && promptInjectionVersions.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip
              label={`${promptInjectionVersions.length} version${promptInjectionVersions.length !== 1 ? 's' : ''}`}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 500, borderColor: 'primary.main', color: 'primary.main' }}
            />
            <Button
              onClick={() => setHistoryOpen(true)}
              size="small"
              variant="contained"
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': { backgroundColor: 'primary.dark', boxShadow: '0 4px 8px rgba(0,0,0,0.15)' }
              }}
            >
              View All
            </Button>
          </Box>
        )}

        {/* ML Prompt Injection Analysis */}
        <div className="mb-4">
          <PromptInjectionMLCard customAnalysis={mlAnalysis} />
        </div>

        {/* LLM Prompt Injection Analysis */}
        <div className="mb-4">
          <PromptInjectionLLMCard customAnalysis={llmAnalysis} />
        </div>

        <VersionHistory
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          versions={versionItems}
          onVersionSelect={handleVersionHistorySelect}
          title="Prompt Injection — All versions"
        />
      </div>
    );
  }
);

PromptInjection.displayName = 'PromptInjection';

export default PromptInjection;
